import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowLeft } from 'lucide-react';

export const NotFoundPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="Page Not Found (404) - Swag Shots"
                description="The page you're looking for doesn't exist. Explore Swag Shots's features, download the app, or return to our homepage to continue your journey."
                url="https://www.swagshots.com/404"
            />

            <div className="min-h-[70vh] flex items-center justify-center py-16 px-4">
                <div className="max-w-2xl w-full text-center">
                    {/* 404 Illustration */}
                    <div className="mb-8">
                        <div className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800 mb-4">
                            404
                        </div>
                        <div className="text-6xl mb-4">🔍</div>
                    </div>

                    {/* Content */}
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Oops! Page Not Found
                    </h1>
                    <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                        The page you're looking for doesn't exist or has been moved.
                        Let's get you back on track!
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                        <Link to="/">
                            <Button
                                size="lg"
                                className="inline-flex items-center gap-2 w-full sm:w-auto"
                            >
                                <Home className="w-5 h-5" />
                                Go to Homepage
                            </Button>
                        </Link>
                        <Link to="/contact">
                            <Button
                                variant="outline"
                                size="lg"
                                className="inline-flex items-center gap-2 w-full sm:w-auto"
                            >
                                <Search className="w-5 h-5" />
                                Contact Support
                            </Button>
                        </Link>
                    </div>

                    {/* Quick Links */}
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            Popular Pages
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Link
                                to="/about"
                                className="text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                                        <span className="text-xl">ℹ️</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">About Us</h3>
                                        <p className="text-sm text-gray-600">Learn about Swag Shots</p>
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/demo"
                                className="text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                                        <span className="text-xl">🎬</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Try Demo</h3>
                                        <p className="text-sm text-gray-600">Experience Swag Shots</p>
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/privacy-policy"
                                className="text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                                        <span className="text-xl">🔒</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Privacy Policy</h3>
                                        <p className="text-sm text-gray-600">Your data protection</p>
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/contact"
                                className="text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                                        <span className="text-xl">💬</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Contact Us</h3>
                                        <p className="text-sm text-gray-600">Get in touch</p>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Back Button */}
                    <button
                        onClick={() => window.history.back()}
                        className="mt-8 text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-2 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Go Back
                    </button>
                </div>
            </div>
        </Layout>
    );
};
